import React, { useState, useEffect } from "react";
import { Lock, Flame, Instagram, Twitter, ExternalLink, ShieldCheck } from "lucide-react";
import { siteConfig, primaryLinks, socialLinks } from "../data/mock";

const iconMap = {
  lock: Lock,
  flame: Flame,
  instagram: Instagram,
  twitter: Twitter,
};

const FireParticle = ({ index }) => {
  const left = Math.random() * 100;
  const delay = Math.random() * 0.8;
  const duration = 0.6 + Math.random() * 0.8;
  const size = 3 + Math.random() * 6;

  return (
    <span
      className="fire-particle"
      style={{
        left: `${left}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        width: `${size}px`,
        height: `${size}px`,
      }}
    />
  );
};

const FogLayer = ({ index }) => {
  const delay = index * 0.4;
  return <div className="fog-layer" style={{ animationDelay: `${delay}s` }} />;
};

const LandingPage = () => {
  const [loaded, setLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = siteConfig.backgroundImage;
  }, []);

  return (
    <div className="landing-root">
      {/* Background Image */}
      <div
        className={`landing-bg ${imageLoaded ? "landing-bg--loaded" : ""}`}
        style={{ backgroundImage: `url(${siteConfig.backgroundImage})` }}
      />
      {/* Gradient Overlay */}
      <div className="landing-overlay" />
      {/* Red Lip Glow */}
      <div className="landing-lip-glow" />
      {/* Grain Texture Overlay */}
      <div className="landing-grain" />

      {/* Content */}
      <div className="landing-content">
        {/* Top Badge */}
        <div className={`landing-badge ${loaded ? "landing-fade-in" : "landing-hidden"}`}>
          <ShieldCheck size={14} />
          <span>Site Oficial Verificado</span>
        </div>

        {/* Name */}
        <h1 className={`landing-name ${loaded ? "landing-slide-up" : "landing-hidden"}`}>
          {siteConfig.name}
        </h1>

        {/* Decorative Line */}
        <div className={`landing-divider ${loaded ? "landing-expand" : "landing-hidden"}`}>
          <div className="landing-divider-line" />
          <span className="landing-divider-diamond">◆</span>
          <div className="landing-divider-line" />
        </div>

        {/* Subtitle */}
        <p className={`landing-subtitle ${loaded ? "landing-fade-in-delay" : "landing-hidden"}`}>
          {siteConfig.subtitle}
        </p>

        {/* Description */}
        <p className={`landing-description ${loaded ? "landing-fade-in-delay2" : "landing-hidden"}`}>
          {siteConfig.description}
        </p>

        {/* Primary CTA Buttons */}
        <div className={`landing-buttons ${loaded ? "landing-slide-up-delay" : "landing-hidden"}`}>
          {primaryLinks.map((link) => {
            const IconComponent = iconMap[link.icon];
            const isHovered = hoveredBtn === link.id;
            return (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`landing-btn-primary ${isHovered ? "landing-btn-primary--fire" : ""}`}
                onMouseEnter={() => setHoveredBtn(link.id)}
                onMouseLeave={() => setHoveredBtn(null)}
                onTouchStart={() => setHoveredBtn(link.id)}
                onTouchEnd={() => setTimeout(() => setHoveredBtn(null), 1500)}
              >
                {/* Fire particles */}
                {isHovered && (
                  <div className="fire-container">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <FireParticle key={i} index={i} />
                    ))}
                  </div>
                )}
                {/* Fog effect */}
                {isHovered && (
                  <div className="fog-container">
                    <FogLayer index={0} />
                    <FogLayer index={1} />
                    <FogLayer index={2} />
                  </div>
                )}
                {/* Ember glow */}
                {isHovered && <div className="ember-glow" />}

                <div className="landing-btn-primary-inner">
                  <div className={`landing-btn-primary-icon ${isHovered ? "icon-fire" : ""}`}>
                    {IconComponent && <IconComponent size={20} />}
                  </div>
                  <div className="landing-btn-primary-text">
                    <span className="landing-btn-primary-label">{link.label}</span>
                    <span className="landing-btn-primary-subtitle">{link.subtitle}</span>
                  </div>
                  <ExternalLink size={16} className="landing-btn-primary-arrow" />
                </div>
                <div className="landing-btn-primary-shine" />
              </a>
            );
          })}
        </div>

        {/* Social Links */}
        <div className={`landing-socials ${loaded ? "landing-fade-in-delay3" : "landing-hidden"}`}>
          {socialLinks.map((link) => {
            const IconComponent = iconMap[link.icon];
            return (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-btn-social"
              >
                {IconComponent && <IconComponent size={18} />}
                <span>{link.label}</span>
              </a>
            );
          })}
        </div>

        {/* Warning */}
        <p className={`landing-warning ${loaded ? "landing-fade-in-delay3" : "landing-hidden"}`}>
          {siteConfig.warning}
        </p>

        {/* Footer */}
        <footer className={`landing-footer ${loaded ? "landing-fade-in-delay3" : "landing-hidden"}`}>
          {siteConfig.copyright}
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
